<script setup lang="ts">
import { ref, watch, computed, onMounted, getCurrentInstance } from "vue";
import { asyncComputed } from '@vueuse/core';

import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";
import type { DropdownItem } from "@/types/ui";

import { EntityType } from "@/types/enums";

type FilterInstancesType = { [key: string]: { [key: string]: any; selected: boolean } };

import { useComponentErrorCapture } from "@/mixins/error";
import translatedProperty from "@/mixins/translatedProperty";
import { isTouchEnabled } from "@/mixins/device";
import { useWindowDimension } from "@/mixins/windowDimension";
import { getIconName } from "@/mixins/icon";
import { useDataHelper } from "@/mixins/dataHelper";

import Draggable from "vuedraggable";

import UCSearch from "@/components/ui/UCSearch.vue";
import FilterTabs from "@/components/ui/FilterTabs.vue";
import EntityListItem from "@/components/elements/entity/EntityListItem.vue";
import ListWithFilter from "@/components/elements/ListWithFilter.vue";
import FilterDropdown from "@/components/elements/FilterDropdown.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import ModalMinimal from "@/components/elements/ModalMinimal.vue";
import QuickEditModal from "@/components/elements/QuickEditModal.vue";
import ListPaging from "@/components/ui/ListPaging.vue";

useComponentErrorCapture();
const { isSmallScreen } = useWindowDimension();
const { objectsDeepEqual } = useDataHelper();

const props = defineProps({
  allEntities: {
    type: Array<ConfiguredEntity>,
    required: true,
  },
  instances: {
    type: Array,
    required: false,
    default: [],
  },
  dragGroup: {
    type: String,
    default: ''
  },
  hasDropdownFilter: {
    type: Boolean,
    default: true,
  },
  hasActionButtons: {
    type: Boolean,
    default: true,
  },
  hasFormActionButtons: {
    type: Boolean,
    default: false,
  },
  hasQuickOptions: {
    type: Boolean,
    default: false,
  },
  hasDropdownMenu: {
    type: Boolean,
    default: true,
  },
  parent: {
    type: String,
    default: ''
  },
  integrationInfo: {
    type: Boolean,
    default: true,
  },
  pagination: {
    type: Object,
    default: {},
  },
  excludeEntityTypes: {
    type: Array,
    default: [],
  },
});

const entityDropdownItems = [
  {
    icon: 'fa-light fa-plus',
    label: 'entity.add_to_page',
    value: 'add_to_page',
  },
  {
    icon: 'fa-light fa-edit',
    label: 'ui.edit',
    value: 'edit',
  },
] as DropdownItem[];

const emit = defineEmits(["changeFilter", "moreEntities", "reloadEntities", "addEntities", "cancel", "changeAssignedEntities", "changePage" , "changePerPage"]);

const entities = ref<ConfiguredEntity[]>([]);

const elEntityList = ref();
const assignedEntities = ref<ConfiguredEntity[]>([]);

const instances = ref<IntegrationInstance[]>([]);

const searchEntity = ref('');
const instance = getCurrentInstance() || {uid: Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000};

const filterInstances = ref<FilterInstancesType>({});
  
const filterEntityTypes = ref<Record<EntityType, { selected: boolean }>>(
  Object.values(EntityType)
    .filter(type => !props.excludeEntityTypes?.includes(type))
    .reduce((obj, type) => {
      obj[type] = { selected: false };
      return obj;
    }, {} as Record<EntityType, { selected: boolean }>)
);

const itemToModify = ref<ConfiguredEntity | null>(null);
const itemToEdit = ref<ConfiguredEntity | null>(null);
const dropdownedEntity = ref<ConfiguredEntity | null>(null);

watch(props, () => {
  setLists();
}, { deep: true });

watch(assignedEntities, (val) => {
  emit('changeAssignedEntities', val);
}, {deep: true});

const iconDrag = asyncComputed(async() => {
  return await getIconName('fa-grip-dots-vertical');
});

const paramEntityTypes = computed(() => {
  return Object.entries(filterEntityTypes.value)
    .filter(([key, value]) => value.selected === true)
    .map(([key, value]) => key)
    .join(',');
});

const paramInstances = computed(() => {
  return Object.entries(filterInstances.value)
    .filter(([key, value]) => value.selected === true)
    .map(([key, value]) => key)
    .join(',');
});

const buttonStyle = computed(() => {
  return isSmallScreen.value ? 'button--secondary' : 'button--tertiary';
});

const hasBlackBg = computed(() => {
  const themeColoredViews = ['add-group', 'edit-group', 'add-activity', 'add-macro', 'add-activity-group', 'add-integration'];
  return themeColoredViews.includes(props.parent);
});

watch(
  [searchEntity, paramEntityTypes, paramInstances],
  () => {
    const data: EntityFilterData = {
      searchText: searchEntity.value,
      entityTypes: paramEntityTypes.value,
      instances: paramInstances.value
    }
    emit("changeFilter", data);
  },
  { deep: true }
);

function setFilterInstances() {
  if (Object.keys(filterInstances.value).length > 0) {
    return true;
  }

  filterInstances.value = instances.value.reduce(
    (obj: { [key: string]: any }, item: { integration_id: string, [key: string]: any }) => {
      obj[item.integration_id] = {selected: false, ...item};
      return obj;
    },
    {}
  )
}

function goTo(item: DropdownItem, entity: ConfiguredEntity) {
  switch (item.value) {
    case 'add_to_page':
      addToPage(entity)
      break;
    case 'edit':
      editEntity(entity);
      break;
    default:
      return false;
  }
}

function dropdownVisibility(state: boolean, entity: ConfiguredEntity) {
  if (state == true) {
    dropdownedEntity.value = entity;
  } else {
    dropdownedEntity.value = null;
  }
}

function disableEntityType(keyEntityType: string, collection: { [key: string]: any }) {
  if (collection) {
    collection[keyEntityType].selected = false;
  }
}

function assignAllEntities(array: ConfiguredEntity []) {
  assignedEntities.value = []
  array.forEach(obj => {
    assignedEntities.value.push(obj);
  });
}

function deAssignAllEntities() {
  assignedEntities.value = [];
}

function addEntitiesToList() {
  if (assignedEntities.value.length > 0) {
    emit("addEntities", assignedEntities.value);
    deAssignAllEntities();
  }
}

function isAssignedItem(entity: ConfiguredEntity) {
  return assignedEntities.value.findIndex((item: ConfiguredEntity) => item.entity_id === entity.entity_id) > -1;
}

function toggleItemCheckbox(entity: ConfiguredEntity) {
  const itemIndex = assignedEntities.value.findIndex((item: ConfiguredEntity) => item.entity_id === entity.entity_id);
  if (itemIndex > -1) {
    assignedEntities.value.splice(itemIndex, 1);
  } else {
    assignedEntities.value.push(entity);
  }
}

function setLists() {
  if (!objectsDeepEqual(props.allEntities, entities.value)) {
    entities.value = JSON.parse(JSON.stringify(props.allEntities));

    if (itemToModify.value != null) {
      const newItemData = entities.value.find((item: ConfiguredEntity) => {
        return item.entity_id === itemToModify.value?.entity_id;
      });
      if (newItemData) {
        itemToModify.value = newItemData;
      }
    }
  }

  instances.value = props.instances as IntegrationInstance[];
  setFilterInstances();
}

async function openItemModify(entity: ConfiguredEntity) {
  if (!isTouchEnabled()) {
    return false;
  }

  itemToModify.value = entity;
}

function addToPage(entity: ConfiguredEntity | null) {
  if (entity == null) {
    return;
  }

  const arrayMessage = [entity];
  emit("addEntities", arrayMessage);
  itemToModify.value = null;
}

function editEntity(entity: ConfiguredEntity | null) {
  if (entity == null) {
    return;
  }

  itemToEdit.value = entity;
}

function getEntityList() {
  return entities.value;
}

function changePage(value: number) {
  emit("changePage", value);
}

function changePerPage(value: number) {
  emit("changePerPage", value);
}

function reloadEntities() {
  emit("reloadEntities");
}

onMounted(() => {
  setLists();
})

defineExpose({
  addEntitiesToList,
  getEntityList
});

</script>
<template>
  <ListWithFilter :skip-items-wrapper="true" class="lwf-entity-list">
    <template #form>
      <div class="list-with-filter__form__tools">
        <div class="list-with-filter__search list-with-filter__search--small">
          <UCSearch
            v-model="searchEntity"
            :debouncing="true"
            :small="true"
            :has-sibling="hasDropdownFilter"
            :gray="hasBlackBg"
          />
          <FilterDropdown
            v-if="hasDropdownFilter"
            :filterEntityTypes="filterEntityTypes"
            :filterInstances="filterInstances"
          />
        </div>
        <div v-if="hasFormActionButtons && entities.length > 0" class="list-with-filter__form__actions">
          <button
            v-if="entities.length === assignedEntities.length"
            @click="deAssignAllEntities()"
            :title="$t('ui.clear_all')"
            class="button button--secondary button--icon">
            <i class="fa-light fa-xmark"></i>
          </button>
          <button
            v-else
            @click="assignAllEntities(entities)"
            :title="$t('ui.select_all')"
            class="button button--secondary button--icon">
            <i class="fa-light fa-check"></i>
          </button>
        </div>
      </div>
      <div v-if="hasDropdownFilter" class="list-with-filter__tabs">
        <FilterTabs
          :list="filterEntityTypes"
          @remove-element="(el) => disableEntityType(el, filterEntityTypes)"
        />
        <FilterTabs
          :list="filterInstances"
          @remove-element="(el) => disableEntityType(el, filterInstances)"
        />
      </div>
    </template>
    <template #items>
      <Draggable
        v-model="entities"
        :group="dragGroup"
        :force-fallback="true"
        ref="elEntityList"
        class="lwf-entity-list__items"
        item-key="entity_id"
        handle=".entity-item__drag"
        v-overflow-indicator
      >
        <template #item="{ element }"> 
          <div
            class="entity-item"
            :class="{'entity-item--selected': isAssignedItem(element) === true, 'entity-item--dropdowned': dropdownedEntity?.entity_id == element.entity_id }">
            <EntityListItem
              @click-meta="openItemModify"
              @edit="editEntity"
              :list-item="element"
              :instances="instances"
              :integration-info="integrationInfo"
              :inactive="true"
            >
              <template #checkbox>
                <div class="form-item form-item--checkbox-tick entity-item__checkbox-tick">
                  <input :id="`${instance.uid}-${element.entity_id}-checkbox-tick`" type="checkbox" :checked="isAssignedItem(element)" />
                  <label class="toggle" :for="`${instance.uid}-${element.entity_id}-checkbox-tick`" />
                  <button @click="toggleItemCheckbox(element)" class="button--toggle-tick"></button>
                </div>
              </template>
              <template #options>
                <DropdownMenu
                  v-if="hasDropdownMenu"
                  @item-click="(item) => goTo(item, element)"
                  @show="(state) => dropdownVisibility(state, element)"
                  :list-data="entityDropdownItems"
                  :icon="'fa-light fa-edit'"
                  :title="translatedProperty(element.name) || ''"
                  :on-right="true"
                  class="entity-item__dropdown-menu"
                />
              </template>
            </EntityListItem>
            <span
              v-if="isSmallScreen && hasQuickOptions"
              @click="openItemModify(element)"
              class="entity-item__quick-options">
              <i class="fa-regular fa-ellipsis-vertical"></i>
            </span>
            <span v-else-if="dragGroup.length > 0" class="entity-item__drag">
              <i v-if="iconDrag" class="fa-regular" :class="iconDrag"></i>
            </span> 
          </div>
        </template>
      </Draggable>
    </template>
    <template #footer>
      <div v-show="hasActionButtons" class="list-with-filter__footer">
        <div
          v-show="(!isSmallScreen || assignedEntities.length > 0) && entities.length > 0"
          class="lwf-entity-list__actions"
          :class="{'lwf-entity-list__actions--accent': hasBlackBg}">
          <template v-if="!isSmallScreen">
            <button
              v-if="entities.length === assignedEntities.length"
              @click="deAssignAllEntities()"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-xmark"></i>
              {{ $t("ui.clear_all") }}
            </button>
            <button
              v-else
              @click="assignAllEntities(entities)"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-check"></i>
              {{ $t("ui.select_all") }}
            </button>
          </template>
          <Transition name="opacity-fast">
            <button
              v-show="assignedEntities.length > 0"
              @click="addEntitiesToList()"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-arrow-right"></i>
              {{ $t("ui.add") }}
            </button>
          </Transition>
        </div>
      </div>
    </template>
    <template v-if="pagination.page && pagination.limit" #pagination>
      <ListPaging
        v-if="entities"
        @change-page="changePage"
        @change-per-page="changePerPage"
        :pagination="pagination || {}"
        :length="entities.length"
        :compact="true"
      />
    </template>
  </ListWithFilter>
  <Teleport to="body">
    <ModalMinimal
      @close="itemToModify = null"
      :show="itemToModify != null"
      :name="'modal-entity-options'"
      :title="itemToModify != null ? translatedProperty(itemToModify?.name) : ''"
      class="modal-minimal--item-options"
    >
      <div v-if="itemToModify != null" class="modal-minimal__list">
        <button v-if="dragGroup == 'customise-remote-items'" @click="addToPage(itemToModify)">
          <i class="fa-light fa-plus"></i>
          <span>{{ $t("entity.add_to_page") }}</span>
        </button>
        <button @click="editEntity(itemToModify)">
          <i class="fa-light fa-pencil"></i> 
          <span>{{ $t("ui.edit") }}</span>
        </button>
      </div>
    </ModalMinimal>
  </Teleport>
  <QuickEditModal
    v-if="itemToEdit != null"
    :item="itemToEdit"
    :show-by-parent="true"
    @saved="reloadEntities"
    @closed="itemToEdit = null"
  />
</template>
